import {IS_FIELD_INDICATOR, SHOWABLE_FIELD_DISPLAY_TYPE_IDS, type EditableField, type EditableFieldList, type FieldTypeId, type FondedFieldType, type RecordEditation, type UUID} from 'src/typings';
import type {EditationCallbacks, RecordEditationActionFactory} from '../types';
import type {LogService} from 'core/logging/log.service';
import type {FileService} from '../../file/file.service';
import type {FieldTypeDataService} from 'src/features/record/field-types/field-type-data.service';
import type {Observable} from 'rxjs';
import {BehaviorSubject} from 'rxjs';
import {ofCode} from '../utils';
import {RecordEditationActionsManager} from './record-editation-actions-manager';
import {byId, findFirst} from 'shared/utils/array-utils';
import {exists, isNullOrUndefined} from 'shared/utils/custom-utils';
import {cloneDeep} from 'lodash-es';
import {updateValue} from 'shared/utils/observables-utils';

export class RecordEditationManager {

    readonly #actionsManager: RecordEditationActionsManager;
    readonly #editationState$: BehaviorSubject<RecordEditation>;
    readonly #errorMessages$: BehaviorSubject<string[]>;
    readonly #recordId: UUID;

    private allFondedFieldTypes: FondedFieldType[] = [];
    private oldRecordEditation: RecordEditation;

    constructor(private editationObject: RecordEditation,
                private fileService: FileService,
                private SERVER_URL: string,
                private fieldTypeDataService: FieldTypeDataService,
                private editationCallbacks: EditationCallbacks | undefined,
                private logger: LogService,
                readonly focusedFieldTypeId: FieldTypeId | undefined,
                recordEditationActionFactory: RecordEditationActionFactory) {

        this.#actionsManager = new RecordEditationActionsManager(this, recordEditationActionFactory, logger, editationObject);
        this.#editationState$ = new BehaviorSubject(editationObject);
        this.#errorMessages$ = new BehaviorSubject([]);
        this.#recordId = editationObject.recordId;
    }

    public async initializeEditation(): Promise<void> {
        await this.refreshFondedFieldTypes();
        const decoratedInitialEditationObject = this.decorateEditationObject(this.editationObject);

        this.oldRecordEditation = cloneDeep(decoratedInitialEditationObject);
    }

    public async save(): Promise<void> {
        await this.actions.saveEditation();
    }

    public async publish(): Promise<void> {
        if (!this.editationObject.draft) {
            throw new Error('Record is not a draft, cannot publish');
        }

        await this.actions.publishEditation();
    }

    public async remove(): Promise<void> {
        await this.actions.removeEditation();
    }

    public decorateEditationObject(recordEditation: RecordEditation): RecordEditation {
        recordEditation.usedFieldTypes = this.allFondedFieldTypes
            .filter((fieldType) => recordEditation.fields.some(ofCode(fieldType.code)))
            .filter((fieldType) => this.isFieldTypeShowable(fieldType));
        recordEditation.addableFieldTypes = this.filterAddableFieldTypes(this.allFondedFieldTypes, recordEditation, false);

        this.allFondedFieldTypes.forEach((fieldType) => {
            const datafieldsOfThisNumber = recordEditation.fields.filter(ofCode(fieldType.code));
            for (const field of datafieldsOfThisNumber) {
                if (field.fields) {
                    field.addableFieldTypes = this.filterAddableFieldTypes(fieldType.subfieldTypes, field, true);
                }
            }
        });

        this.updateState(recordEditation);
        return recordEditation;
    }

    public filterExistingEditableIndicators(subfields: EditableField[]): EditableField[] {
        return this.filterExistingEditableSubfields(subfields)
            .filter(IS_FIELD_INDICATOR);
    }

    public filterExistingEditableStandardFields(subfields: EditableField[]): EditableField[] {
        return this.filterExistingEditableSubfields(subfields)
            .filter((subfield) => !IS_FIELD_INDICATOR(subfield));
    }

    private filterExistingEditableSubfields(subfields: EditableField[]): EditableField[] {
        const allEditableSubfieldsTypes = this.allFondedFieldTypes.flatMap((fondedFieldType) => fondedFieldType.subfieldTypes || []);
        const filteredSubfields: EditableField[] = [];

        for (const subfield of subfields) {
            const fondedSubfieldType = findFirst(allEditableSubfieldsTypes, byId(subfield.typeId));

            if (isNullOrUndefined(fondedSubfieldType)) {
                const errMessage = `Subfield type ${subfield.typeId} exists in record but does not exist in editation (probably not defined in styles)`;
                this.logger.warn(errMessage);
                this.reportErrorMessageIfNotYetReported(errMessage);
            }

            if (exists(fondedSubfieldType) && this.isFieldTypeShowable(fondedSubfieldType)) {
                filteredSubfields.push(subfield);
            }
        }

        return filteredSubfields;
    }

    public getFondedSubfieldTypeById(subfieldTypeId: FieldTypeId): FondedFieldType {
        const allFondedSubfieldsTypes = this.allFondedFieldTypes.flatMap((fondedFieldType) => fondedFieldType.subfieldTypes || []);
        const fondedSubfieldType = findFirst(allFondedSubfieldsTypes, byId(subfieldTypeId));

        if (isNullOrUndefined(fondedSubfieldType)) {
            throw new Error(`Subfield type ${subfieldTypeId} exists in record but does not exist in editation (probably not defined in styles)`);
        }

        return fondedSubfieldType;
    }

    public handleSubfieldFilesDropped(subfield: EditableField, fileList: FileList): void {
        if (fileList.length > 1) {
            throw new Error('Cannot set more than 1 file to 1 subfield');
        }

        this.fileService.uploadFileToDirectory(this.editationObject.directoryId, fileList[0])
            .then((file) => {
                const newValue = `${this.SERVER_URL}/media-viewer?rootDirectory=${file.directory.id}#!?file=${file.id}`;
                this.actions.setFieldValue(subfield, newValue);
            });
    }

    public async refreshFondedFieldTypes(): Promise<void> {
        this.allFondedFieldTypes = await this.fieldTypeDataService.queryByRecordEditation(this.editationObject.id);
    }

    public get editationObjectBeforeCurrentEditation(): RecordEditation {
        return this.oldRecordEditation;
    }

    public get actions(): RecordEditationActionsManager {
        return this.#actionsManager;
    }

    public get editationState$(): Observable<RecordEditation> {
        return this.#editationState$.asObservable();
    }

    public get editationStateValue(): RecordEditation {
        return this.#editationState$.getValue();
    }

    public get errorMessages$(): Observable<string[]> {
        return this.#errorMessages$.asObservable();
    }

    public get callbacks(): EditationCallbacks {
        return this.editationCallbacks;
    }

    public get recordId(): UUID {
        return this.#recordId;
    }

    public reportErrorMessageIfNotYetReported(message: string): void {
        updateValue(this.#errorMessages$, (currentErrorMessages) => {
            return currentErrorMessages.includes(message) ? currentErrorMessages : [...currentErrorMessages, message];
        });
    }

    public reportErrorMessage(message: string): void {
        updateValue(this.#errorMessages$, (currentErrorMessages) => [...currentErrorMessages, message]);
    }

    private updateState(editationObject: RecordEditation) {
        this.#editationState$.next(editationObject);
    }

    private filterAddableFieldTypes(fondedFieldTypes: FondedFieldType[], fieldContainer: EditableFieldList, withRepeatable: boolean): FondedFieldType[] {
        return fondedFieldTypes
            .filter((fieldType) => (fieldType.repeatable && withRepeatable) || !fieldContainer.fields.some(ofCode(fieldType.code)))
            .filter((fieldType) => this.isFieldTypeShowable(fieldType));
    }

    private isFieldTypeShowable(fieldType: FondedFieldType) {
        return SHOWABLE_FIELD_DISPLAY_TYPE_IDS.includes(fieldType.displayType.id);
    }
}